import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, Edit3, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate, useParams } from 'react-router-dom';

export default function PayrollDetail() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [confirmedPayroll, setConfirmedPayroll] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState({
    basic_salary: 0,
    allowances: 0
  });
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const fetchData = async () => {
    try {
      // Fetch employee details
      const empResponse = await axios.get(`${API}/employees/${employeeId}`);
      setEmployee(empResponse.data);

      // Check if payroll is already confirmed for this MONTH
      const date = new Date(today);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const confirmedResponse = await axios.get(`${API}/payroll/confirmed?year=${year}&month=${month}`);
      const confirmedForEmployee = confirmedResponse.data.find(p => p.employee_id === employeeId);
      
      if (confirmedForEmployee) {
        setConfirmedPayroll(confirmedForEmployee);
        setPayrollData(confirmedForEmployee);
        setEditableData({
          basic_salary: confirmedForEmployee.basic_salary || 0,
          allowances: confirmedForEmployee.allowances || 0
        });
      } else {
        // Calculate payroll
        const calcResponse = await axios.post(`${API}/payroll/calculate`, {
          employee_id: employeeId,
          date: today
        });
        setPayrollData(calcResponse.data);
        setEditableData({
          basic_salary: calcResponse.data.basic_salary || 0,
          allowances: calcResponse.data.allowances || 0
        });
      }
    } catch (error) {
      toast.error('Failed to fetch payroll details');
      navigate('/dashboard/payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      // First, update the employee's salary in the database
      await axios.put(`${API}/employees/${employeeId}`, {
        salary_basic: parseFloat(editableData.basic_salary) || 0,
        salary_allowances: parseFloat(editableData.allowances) || 0
      });
      
      // Then recalculate payroll with new values
      const calcResponse = await axios.post(`${API}/payroll/calculate`, {
        employee_id: employeeId,
        date: today
      });
      
      setPayrollData(calcResponse.data);
      setIsEditing(false);
      toast.success('Salary updated and payroll recalculated!');
      
      // Refresh employee data
      const empResponse = await axios.get(`${API}/employees/${employeeId}`);
      setEmployee(empResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update and recalculate payroll');
    }
  };

  const handleConfirmPayroll = async () => {
    try {
      await axios.post(`${API}/payroll/confirm`, {
        ...payrollData,
        basic_salary: parseFloat(editableData.basic_salary) || 0,
        allowances: parseFloat(editableData.allowances) || 0,
        gross_salary: (parseFloat(editableData.basic_salary) || 0) + (parseFloat(editableData.allowances) || 0),
        date: today
      });
      toast.success('Payroll confirmed successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payroll');
    }
  };

  const handleUnconfirmPayroll = async () => {
    if (!confirmedPayroll) return;
    
    try {
      await axios.delete(`${API}/payroll/confirmed/${confirmedPayroll.payroll_id}`);
      toast.success('Payroll unconfirmed successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to unconfirm payroll');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  if (!employee || !payrollData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">No payroll data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/payroll')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Payroll</span>
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Payroll Details</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {employee.full_name} - {new Date(today).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        {confirmedPayroll ? (
          <span className="flex items-center space-x-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded-lg">
            <CheckCircle className="h-5 w-5" />
            <span className="font-semibold">Confirmed</span>
          </span>
        ) : (
          <span className="flex items-center space-x-2 px-4 py-2 bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 rounded-lg">
            <XCircle className="h-5 w-5" />
            <span className="font-semibold">Pending Confirmation</span>
          </span>
        )}
      </div>

      {/* Payslip */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
        {/* Header Section */}
        <div className="border-b border-slate-200 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-900">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">PAYSLIP</h3>
              <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                <p><span className="font-medium">Employee ID:</span> {employee.employee_id}</p>
                <p><span className="font-medium">Employee Name:</span> {employee.full_name}</p>
                <p><span className="font-medium">Department:</span> {employee.department || 'N/A'}</p>
                <p><span className="font-medium">Position:</span> {employee.position || 'N/A'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 dark:text-slate-400">Pay Date</p>
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {new Date(today).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Payslip Body */}
        <div className="p-6 space-y-6">
          {/* Earnings */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase">Earnings</h4>
              {!confirmedPayroll && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(!isEditing)}
                  className="text-blue-600 dark:text-blue-400"
                >
                  {isEditing ? (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      Save & Recalculate
                    </>
                  ) : (
                    <>
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <span className="text-slate-900 dark:text-slate-100">Basic Salary</span>
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-slate-600 dark:text-slate-400">RM</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={editableData.basic_salary}
                      onChange={(e) => setEditableData({ ...editableData, basic_salary: e.target.value })}
                      className="w-32 h-8 text-right dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                ) : (
                  <span className="font-semibold text-slate-900 dark:text-slate-100">RM {Number(editableData.basic_salary || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <span className="text-slate-900 dark:text-slate-100">Allowances</span>
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-slate-600 dark:text-slate-400">RM</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={editableData.allowances}
                      onChange={(e) => setEditableData({ ...editableData, allowances: e.target.value })}
                      className="w-32 h-8 text-right dark:bg-slate-900 dark:border-slate-600"
                    />
                  </div>
                ) : (
                  <span className="font-semibold text-slate-900 dark:text-slate-100">RM {Number(editableData.allowances || 0).toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between py-3 bg-slate-50 dark:bg-slate-900 px-3 rounded-lg mt-2">
                <span className="font-bold text-slate-900 dark:text-slate-100">Gross Salary</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 text-lg">
                  RM {(Number(editableData.basic_salary || 0) + Number(editableData.allowances || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Claims Reimbursement */}
          {payrollData.claims_reimbursement > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase mb-3">Claims & Reimbursements</h4>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-700">
                  <div>
                    <p className="text-slate-900 dark:text-slate-100">Approved Claims ({payrollData.approved_claims_count || 0})</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Reimbursement for this month</p>
                  </div>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">+ RM {(payrollData.claims_reimbursement || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Deductions */}
          <div>
            <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase mb-3">Deductions</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-slate-900 dark:text-slate-100">EPF (Employee)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Employer: RM {(payrollData.epf_employer || 0).toFixed(2)}</p>
                </div>
                <span className="font-semibold text-red-600 dark:text-red-400">- RM {(payrollData.epf_employee || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-slate-900 dark:text-slate-100">SOCSO (Employee)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Employer: RM {(payrollData.socso_employer || 0).toFixed(2)}</p>
                </div>
                <span className="font-semibold text-red-600 dark:text-red-400">- RM {(payrollData.socso_employee || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-slate-900 dark:text-slate-100">EIS (Employee)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Employer: RM {(payrollData.eis_employer || 0).toFixed(2)}</p>
                </div>
                <span className="font-semibold text-red-600 dark:text-red-400">- RM {(payrollData.eis_employee || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <div>
                  <p className="text-slate-900 dark:text-slate-100">PCB Tax (Monthly Tax Deduction)</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Income Tax</p>
                </div>
                <span className="font-semibold text-red-600 dark:text-red-400">- RM {(payrollData.pcb_tax || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-3 bg-red-50 dark:bg-red-900/20 px-3 rounded-lg mt-2">
                <span className="font-bold text-slate-900 dark:text-slate-100">Total Deductions</span>
                <span className="font-bold text-red-600 dark:text-red-400 text-lg">- RM {(payrollData.total_deductions || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Net Salary */}
          <div className="border-t-2 border-slate-300 dark:border-slate-600 pt-4">
            <div className="flex justify-between items-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
              <span className="text-xl font-bold text-slate-900 dark:text-slate-100">Net Salary</span>
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">RM {(payrollData.net_salary || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Bank Details */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase mb-3">Payment Details</h4>
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Bank Name:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{employee.bank_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Account Number:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{employee.bank_account || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-slate-200 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-900 flex justify-end space-x-3">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditableData({
                    basic_salary: payrollData.basic_salary || 0,
                    allowances: payrollData.allowances || 0
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRecalculate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save & Recalculate
              </Button>
            </>
          ) : confirmedPayroll ? (
            <Button
              variant="destructive"
              onClick={handleUnconfirmPayroll}
              className="bg-red-600 hover:bg-red-700"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Unconfirm Payroll
            </Button>
          ) : (
            <Button
              onClick={handleConfirmPayroll}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Payroll
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
